/**
 * Pipeline board component.
 *
 * Uses usePipelineDragDrop with collision detection and sensors,
 * rendering PipelineColumn with the updated header design.
 */

import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import { Plus } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { logger } from '@/lib/logger';

import { PipelineDragOverlay } from '../../pipeline/components/PipelineDragOverlay';
import { usePipelineDragDrop } from '../../pipeline/hooks/usePipelineDragDrop';
import { usePipelineSensors } from '../../pipeline/hooks/usePipelineSensors';
import { useResortNotices } from '../../pipeline/hooks/useResortNotices';
import type { PipelineStage } from '../../pipeline/types';
import { createColumnFirstCollision } from '../../pipeline/utils/collisionDetection';
import { buildDragAnnouncements } from '../../pipeline/utils/dragAnnouncements';
import { reportDragIdentityMismatch } from '../../pipeline/utils/dragIdentityMismatch';
import { AddStageModal, type AddStageSubmitData } from './AddStageModal';
import { DndActiveSentinel } from './DndActiveSentinel';
import type { PipelineBoardProps } from './PipelineBoard.types';
import { PipelineColumn } from './PipelineColumn';
import { usePipelineBoardItems } from './usePipelineBoardItems';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Minimum number of stages a pipeline must keep. Delete is disabled when the
 * pipeline has fewer than this many stages. Pipelines with fewer stages are
 * not useful as a workflow.
 */
const MIN_PIPELINE_STAGES = 3;

/** Stable empty Set fallback for stages absent from a per-stage ID record. */
const EMPTY_ID_SET: ReadonlySet<string | number> = new Set();

/**
 * A dropped card re-sorts in the SAME frame as the drop. Nothing about the
 * board's layout may move on a timer after that.
 *
 * ⚠️ Do not re-introduce a post-drop hold, and do not auto-scroll a column to
 * follow a re-sorted card. Both existed (`RESORT_HOLD_MS` 850ms + a 520ms eased
 * column scroll) and both were removed, because the board stays fully
 * interactive while they run and the user's next click has to land on a moving
 * target:
 *
 *   t+700ms   user visually picks a card
 *   t+850ms   hold expires — card re-sorts AND the column scrolls (2200px, in a
 *             reproduction against a 33-card column; #10217 measured 3096px)
 *   t+900ms   the click lands ~200ms after the eye did, on whatever card the
 *             reflow slid under the cursor
 *
 * dnd-kit binds the draggable at `mousedown`, so it faithfully drags the card
 * that arrived under the pointer — and a record the user never touched gets
 * moved to a new stage. Silent, and only visible if you happen to notice.
 *
 * The 850ms hold and the scroll were added to explain and follow the re-sort.
 * The badge below does the explaining without moving anything; a re-sort fused
 * to the user's own drop needs no following, because their attention is already
 * there. See the "Resorting by …" notice and HIGHLIGHT_MS.
 */

/**
 * How long the "Resorting by …" notice stays visible.
 *
 * Deliberately OUTLIVES the flash rather than ending with it:
 *
 *   0ms     drop — card re-sorts immediately, notice fades in
 *   1100ms  flash ends (HIGHLIGHT_MS)
 *   1880ms  notice starts fading (RESORT_NOTICE_MS - RESORT_NOTICE_FADE_MS)
 *   2200ms  notice gone — still AFTER the flash, by 1100ms
 *
 * An earlier version ended the explanation at the moment the card moved — the
 * label disappeared right when the thing it was explaining happened, which is
 * precisely backwards.
 *
 * INVARIANT: this must stay greater than HIGHLIGHT_MS, or the badge disappears
 * before the flash it is explaining.
 */
const RESORT_NOTICE_MS = 2200;

/**
 * How long the badge's fade-out runs before it unmounts.
 *
 * The badge needs an explicit exit phase: `animate-out fade-out` (the project
 * idiom — see lib/base/tooltip/tooltip.tsx:487, popover.tsx:41) only plays if
 * the element is still mounted, and react-aria's `isExiting` isn't available
 * here because this board owns the unmount rather than an overlay primitive.
 * So the notice timer flips to an exiting phase first, then unmounts once the
 * fade has played — otherwise the badge just pops out of existence.
 */
const RESORT_NOTICE_FADE_MS = 320;

/**
 * How long the `pipeline-resort-flash` class stays on the re-sorted card.
 *
 * Must match the animation duration in index.css — leaving the class on after
 * the animation ends would keep a spent `outline` declaration on the card for
 * no reason. Shorter than the generic deep-link flash (3s) because the card
 * re-sorts under the user's own cursor; this only confirms where it landed.
 *
 * Paint only — `outline` does not affect layout or hit-testing, so unlike the
 * removed scroll this cannot move anything under the pointer.
 */
const HIGHLIGHT_MS = 1100;

/**
 * How long card clicks are ignored after a drag ends.
 *
 * A drag can travel past the 8px activation threshold and still release
 * INSIDE the same card. The browser then dispatches a `click` on that card
 * AFTER dnd-kit's teardown has flushed — so `useSortable().isDragging` is
 * already false, the card's onClick is re-attached, and the record slideout
 * opens off the tail of a drag the user just performed. The suppression
 * window absorbs that synthetic click; a deliberate re-click of the card
 * takes a human longer than this to produce.
 */
const POST_DRAG_CLICK_SUPPRESSION_MS = 250;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type { PipelineBoardProps };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PipelineBoard<T>({
  items,
  stages,
  getItemId,
  getItemStage,
  setItemStage,
  renderCard,
  onStageChange,
  onItemClick,
  onRefresh,
  stageMetrics,
  onAddToStage,
  isFiltered,
  loading,
  sortBy = 'ahoy_created_at',
  sortOrder = 'desc',
  sortLabel,
  openItemId = null,
  onRenameStage,
  onAddStage,
  onMoveStage,
  onReorderStages,
  onRecolorStage,
  onDeleteStage,
  canEditStage = false,
  canDeleteStage = false,
  showProbability = false,
  initialScrollToStageId,
  isUpdating = false,
  footerCalculations,
  footerPropertySchemas,
}: PipelineBoardProps<T>) {
  const isMobile = !useBreakpoint('md');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeItem, setActiveItem] = useState<T | null>(null);
  // Tracks an in-flight column drag (separate from card drag-state above).
  // Holds the stage ID being dragged so we can route handlers and skip the
  // card-drag side effects when the active draggable is a column.
  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
  // Synchronous optimistic order applied on column drop, before the parent's
  // mutation propagates a new `stages` prop. Without this, the dropped column
  // briefly snaps back to its old DOM slot during the cancelQueries +
  // setQueryData async tick, which reads as a jitter / boomerang. Mirrors
  // the `optimisticItems` pattern used by usePipelineDragDrop for cards.
  const [optimisticStageOrder, setOptimisticStageOrder] = useState<
    string[] | null
  >(null);
  // Item to flash after it has been re-sorted, so the eye can reacquire it.
  const [highlightedItemId, setHighlightedItemId] = useState<
    string | number | null
  >(null);
  // The visible "Resorting by …" notice, which outlives the flash it explains
  // (see RESORT_NOTICE_MS). Maps item → stage. Two-phase fade-out timer
  // sequence, per-item cancellation-on-re-drag, and unmount cleanup all live
  // in the hook — see useResortNotices for the exact sequencing.
  const {
    resortNotices,
    exitingNoticeIds,
    triggerResortNotice,
    cancelResortNotice,
    cancelAllResortNotices: cancelAllResortNoticesOnly,
  } = useResortNotices(RESORT_NOTICE_MS, RESORT_NOTICE_FADE_MS);
  const [collapsedColumns, setCollapsedColumns] = useState<Set<string>>(
    new Set()
  );
  const [isAddStageModalOpen, setIsAddStageModalOpen] = useState(false);
  // When the modal was opened from a column dropdown (Add stage left/right),
  // remember which stage and side so we can pass it to onAddStage on submit.
  // Null when the modal is for the trailing "+ Add stage" affordance.
  const [insertContext, setInsertContext] = useState<{
    relativeStageId: string;
    side: 'left' | 'right';
  } | null>(null);

  const handleOpenAddStage = useCallback(() => {
    setInsertContext(null);
    setIsAddStageModalOpen(true);
  }, []);

  const handleOpenInsertStage = useCallback(
    (relativeStageId: string, side: 'left' | 'right') => {
      setInsertContext({ relativeStageId, side });
      setIsAddStageModalOpen(true);
    },
    []
  );

  const handleCloseAddStage = useCallback(() => {
    setIsAddStageModalOpen(false);
    setInsertContext(null);
  }, []);

  const handleAddStageSubmit = useCallback(
    (data: AddStageSubmitData) => {
      // Close immediately so the optimistic update reads as instant. The
      // mutation hook in the parent handles rollback on failure (including a
      // toast), so we don't need to wait for the round-trip here.
      const position = insertContext ?? undefined;
      setIsAddStageModalOpen(false);
      setInsertContext(null);
      onAddStage?.(data, position);
    },
    [onAddStage, insertContext]
  );

  const showAddStageButton = canEditStage && Boolean(onAddStage);
  const canReorderColumns = canEditStage && Boolean(onReorderStages);

  // Reset collapse on mobile/desktop switch
  useEffect(() => {
    setCollapsedColumns(new Set());
  }, [isMobile]);

  /** Clear every notice AND the flash — used when an API failure rolls a move back. */
  const cancelAllResortNotices = useCallback(() => {
    cancelAllResortNoticesOnly();
    setHighlightedItemId(null);
  }, [cancelAllResortNoticesOnly]);

  const toggleColumnCollapse = useCallback(
    (stageId: string) => {
      if (activeItem) {
        return;
      }
      setCollapsedColumns(prev => {
        const next = new Set(prev);
        if (next.has(stageId)) {
          next.delete(stageId);
        } else {
          next.add(stageId);
        }
        return next;
      });
    },
    [activeItem]
  );

  // Scroll to first column with results when filtered (only when target stage changes)
  const lastScrolledStageRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isFiltered || !scrollContainerRef.current) {
      lastScrolledStageRef.current = null;
      return;
    }

    const firstStageWithItems = stages.findIndex(stage => {
      return items.some(item => getItemStage(item) === stage.id);
    });

    if (firstStageWithItems < 0) {
      return;
    }

    const targetStageId = stages[firstStageWithItems].id;
    if (targetStageId === lastScrolledStageRef.current) {
      return;
    }
    lastScrolledStageRef.current = targetStageId;

    const column = scrollContainerRef.current.children[firstStageWithItems] as
      | HTMLElement
      | undefined;
    column?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [isFiltered, items, stages, getItemStage]);

  // Effective stages array — applies the synchronous optimisticStageOrder
  // when present so the column dropped via drag-and-drop renders at its new
  // slot immediately, before the parent's React Query optimistic update
  // propagates back via `stages`. Once the prop catches up, the reconcile
  // effect below clears `optimisticStageOrder` and we fall back to `stages`.
  //
  // Computed BEFORE the usePipelineDragDrop call below (order matters within
  // the render body): the hook takes `validStageIds` so it can ignore/clear
  // optimistic overrides that target a stage no longer on the board.
  const effectiveStages = useMemo(() => {
    if (!optimisticStageOrder) {
      return stages;
    }
    const byId = new Map(stages.map(s => [s.id, s] as const));
    const ordered: PipelineStage[] = [];
    for (const id of optimisticStageOrder) {
      const stage = byId.get(id);
      if (stage) {
        ordered.push(stage);
      }
    }
    // Fall back to prop order if any IDs went missing (e.g., a stage was
    // deleted after the drag) — safer than rendering a partial list.
    if (ordered.length !== stages.length) {
      return stages;
    }
    return ordered;
  }, [stages, optimisticStageOrder]);

  // Reconcile: clear the optimistic order once the `stages` prop reflects it.
  // The mutation success path eventually pushes the new order through
  // useUpdatePipelineStages's onMutate; once stages.map(s.id) matches our
  // optimistic order we can drop the local override.
  useEffect(() => {
    if (!optimisticStageOrder) {
      return;
    }
    if (stages.length !== optimisticStageOrder.length) {
      // Length mismatch (add/delete happened mid-flight) — drop the override
      // and let the prop be the source of truth.
      setOptimisticStageOrder(null);
      return;
    }
    for (let i = 0; i < stages.length; i++) {
      if (stages[i].id !== optimisticStageOrder[i]) {
        return;
      }
    }
    setOptimisticStageOrder(null);
  }, [stages, optimisticStageOrder]);

  // Memoized stage ID set/list — avoids recreating on every dragOver call
  const stageIds = useMemo(
    () => effectiveStages.map(s => s.id),
    [effectiveStages]
  );
  const stageIdSet = useMemo(() => new Set(stageIds), [stageIds]);

  // DnD state
  const {
    dragState,
    optimisticItems,
    handleDragStart: onDragStartHook,
    handleDragOver: onDragOverHook,
    handleDragEnd: onDragEndHook,
    handleDragCancel,
  } = usePipelineDragDrop({
    items,
    getItemId,
    getItemStage,
    setItemStage,
    onStageChange,
    onRefresh,
    onRollback: cancelAllResortNotices,
    // An optimistic override targeting a stage the board no longer renders
    // (teammate deleted it mid-drag / mid-flight) would otherwise exclude the
    // card from every column — it vanishes until the safety timeout clears
    // the override. With the valid-stage set, the hook ignores and clears
    // such overrides so the card falls back to its server stage instantly.
    validStageIds: stageIdSet,
  });

  // Refs for values used in handleDragEnd — avoids recreating the callback
  // on every dragOver (which changes dragState.overStage)
  const dragStateRef = useRef(dragState);
  dragStateRef.current = dragState;
  const activeItemRef = useRef(activeItem);
  activeItemRef.current = activeItem;
  const draggedColumnIdRef = useRef(draggedColumnId);
  draggedColumnIdRef.current = draggedColumnId;

  // Timestamp of the most recent drag end/cancel — clicks landing within
  // POST_DRAG_CLICK_SUPPRESSION_MS of it are the browser's post-drag click
  // on the drop target, not an intent to activate it.
  const lastDragEndedAtRef = useRef(0);

  // Board-level post-drag click suppression. A drag can release inside the
  // same element it started on (a jiggle-drag), and the browser then fires a
  // trailing `click` on the drop target after dnd-kit's teardown flushes.
  // That click carries no user intent, yet it would open the source record
  // (a card) or enter inline rename (a column title). Capturing here — above
  // every column and its controls — swallows any click landing within
  // POST_DRAG_CLICK_SUPPRESSION_MS of a drag end/cancel, so one guard covers
  // cards, column titles, and every other control uniformly (a per-card guard
  // missed the column controls entirely).
  const suppressPostDragClickCapture = useCallback(
    (event: React.MouseEvent) => {
      if (
        Date.now() - lastDragEndedAtRef.current <
        POST_DRAG_CLICK_SUPPRESSION_MS
      ) {
        logger.debug('pipeline-dnd: suppressed post-drag click-through');
        event.stopPropagation();
      }
    },
    []
  );

  // Self-healing for the skipped-callback teardown (see DndActiveSentinel):
  // whenever dnd-kit reports no active drag, clear any drag state of ours
  // that survived. On the normal path our handlers already cleared everything
  // in the same commit dnd-kit's `active` nulled, so the refs are null and
  // this is a no-op. Residue here means dnd-kit ended a drag without calling
  // onDragEnd/onDragCancel — the exact leak that makes a stale placeholder
  // render alongside the next drag ("two items dragging at once").
  const handleDndActiveCleared = useCallback(() => {
    const leakedItem = activeItemRef.current;
    const leakedColumnId = draggedColumnIdRef.current;
    const leakedHookDrag = dragStateRef.current.activeItem !== null;
    if (leakedItem === null && leakedColumnId === null && !leakedHookDrag) {
      return;
    }
    logger.warn(
      'pipeline-dnd: drag ended without onDragEnd/onDragCancel — clearing leaked drag state',
      {
        leakedActiveItemId:
          leakedItem !== null ? String(getItemId(leakedItem)) : null,
        leakedColumnId,
        leakedHookDrag,
      }
    );
    // A leaked teardown means dnd-kit fired neither onDragEnd nor onDragCancel,
    // so nothing else opened the post-drag click-suppression window. Open it
    // here before clearing state — otherwise the browser's trailing click on
    // the drop target (source card, or a column title → inline rename) slips
    // through unguarded.
    lastDragEndedAtRef.current = Date.now();
    handleDragCancel();
    setActiveItem(null);
    setDraggedColumnId(null);
  }, [getItemId, handleDragCancel]);

  // Sensors
  const sensors = usePipelineSensors();

  // Per-stage rendering data — grouped items, id→item lookup, footer lines,
  // and notice/highlight scoping. See usePipelineBoardItems for the "why"
  // behind each piece (duplicate-id handling, the debug tripwire, per-stage
  // notice scoping to avoid board-wide PipelineColumn re-renders, etc.).
  const {
    itemsByStage,
    itemByIdMap,
    stageFooterLines,
    resortNoticeItemIdsByStage,
    exitingNoticeIdsByStage,
    highlightedItemStageId,
  } = usePipelineBoardItems({
    optimisticItems,
    effectiveStages,
    getItemId,
    getItemStage,
    sortBy,
    sortOrder,
    footerCalculations,
    footerPropertySchemas,
    resortNotices,
    exitingNoticeIds,
    highlightedItemId,
    activeItemRef,
    draggedColumnIdRef,
  });

  // The single stage containing the open (slideout) item, if any. Only that
  // column receives a non-null `openItemId` — every other column gets
  // `null`, a stable primitive that `Object.is` already compares correctly.
  const openItemStageId = useMemo(() => {
    if (openItemId === null) {
      return null;
    }
    const item = itemByIdMap.get(String(openItemId));
    return item ? getItemStage(item) : null;
  }, [openItemId, itemByIdMap, getItemStage]);

  // Collision detection
  const collisionDetection = useMemo(
    () => createColumnFirstCollision(stageIds),
    [stageIds]
  );

  // --- Drag handlers ---

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event;
      // Column drag (stage being reordered) — skip the card-drop hooks. The
      // sortable transforms handle the visual reflow during drag; nothing
      // else needs tracking until drop.
      if (active.data.current?.type === 'column') {
        logger.debug('pipeline-dnd: column drag start', {
          columnId: String(active.id),
        });
        setDraggedColumnId(String(active.id));
        return;
      }
      const draggedItem = itemByIdMap.get(String(active.id));
      reportDragIdentityMismatch(event, String(active.id), draggedItem != null);
      logger.debug('pipeline-dnd: drag start', {
        activeId: String(active.id),
        resolvedInMap: draggedItem != null,
        originalStage: draggedItem ? getItemStage(draggedItem) : null,
      });
      if (draggedItem) {
        // Re-dragging a card that still has a notice up: drop the stale label
        // rather than let it annotate a move the user has already superseded.
        cancelResortNotice(getItemId(draggedItem));
        setActiveItem(draggedItem);
        onDragStartHook(draggedItem);
      }
    },
    [itemByIdMap, onDragStartHook, cancelResortNotice, getItemId, getItemStage]
  );

  // Scroll a column into view by stage ID. Works on both mobile and
  // desktop — used during card drag-over to keep the active drop target
  // visible AND for deep links (e.g., a stage badge elsewhere navigating
  // to the pipeline view with `?stage=<id>`).
  const scrollToStage = useCallback(
    (stageId: string) => {
      if (!scrollContainerRef.current) {
        return;
      }
      const stageIndex = effectiveStages.findIndex(s => s.id === stageId);
      if (stageIndex < 0) {
        return;
      }
      const column = scrollContainerRef.current.children[stageIndex] as
        | HTMLElement
        | undefined;
      column?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    },
    [effectiveStages]
  );

  // Deep-link scroll: when an `initialScrollToStageId` prop arrives (or the
  // stage list resolves with that ID present), scroll the matching column
  // into view exactly once. Tracked by a ref so re-renders with the same ID
  // don't re-trigger the scroll (which would fight the user's interactions).
  const lastDeepLinkedStageRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialScrollToStageId) {
      return;
    }
    if (lastDeepLinkedStageRef.current === initialScrollToStageId) {
      return;
    }
    if (!effectiveStages.some(s => s.id === initialScrollToStageId)) {
      return;
    }
    lastDeepLinkedStageRef.current = initialScrollToStageId;
    scrollToStage(initialScrollToStageId);
  }, [initialScrollToStageId, effectiveStages, scrollToStage]);

  // Clear the flash once its animation has played.
  //
  // Flash only — the board deliberately does NOT scroll to follow a re-sorted
  // card. It used to (horizontally via scrollToStage, vertically via a 520ms
  // animateScrollTop tween centring the card in its column), which is what
  // moved thousands of pixels of layout under a user who was mid-aim. See the
  // note above RESORT_NOTICE_MS before adding any of it back.
  useEffect(() => {
    if (highlightedItemId === null) {
      return;
    }
    const timer = setTimeout(() => setHighlightedItemId(null), HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [highlightedItemId]);

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over) {
        return;
      }

      // Column drag — sortable strategy already transforms columns into their
      // hover positions; no side effects needed (no auto-scroll, no
      // collapsed-column expansion, no card-drop hook).
      if (active.data.current?.type === 'column') {
        return;
      }

      const activeId = String(active.id);
      const overId = String(over.id);

      if (activeId === overId) {
        return;
      }

      let targetStageId: string | undefined;

      if (stageIdSet.has(overId)) {
        targetStageId = overId;
      } else {
        const overItem = itemByIdMap.get(overId);
        if (overItem) {
          targetStageId = getItemStage(overItem);
        }
      }

      if (targetStageId) {
        onDragOverHook(targetStageId);
        // Auto-scroll during drag-over is only useful on mobile (carousel
        // shows one column at a time). On desktop all columns are visible
        // so an automatic horizontal scroll feels jarring. (Scrolling is safe
        // mid-drag: dnd-kit compensates scrollable-ancestor offsets.)
        if (isMobile) {
          scrollToStage(targetStageId);
        }
        // ⚠️ Do NOT auto-expand a collapsed target column here. Expanding
        // mid-drag animates its width 56px → 347px and shifts every column to
        // its right, but dnd-kit measured all droppable rects at DRAG START
        // and only re-measures when the droppable LIST changes (strategy
        // WhileDragging, frequency 'optimized' — no interval; ResizeObserver
        // catches only the expanded column's own size, not its neighbors'
        // positions). Every subsequent pointerWithin test then runs against
        // pre-expansion rects, so drops resolve one column off. A collapsed
        // column is already a valid, ring-highlighted drop target at 56px —
        // it expands ON DROP instead (see handleDragEnd).
      }
    },
    [
      stageIdSet,
      itemByIdMap,
      getItemStage,
      onDragOverHook,
      scrollToStage,
      isMobile,
    ]
  );

  // Refs so handleDragEnd doesn't need to depend on stageIds (which changes
  // when a stage is added/removed/reordered) and onReorderStages
  const stageIdsRef = useRef(stageIds);
  stageIdsRef.current = stageIds;
  const onReorderStagesRef = useRef(onReorderStages);
  onReorderStagesRef.current = onReorderStages;

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      // Both card and column drags can release inside a clickable element and
      // produce a trailing browser click — start the suppression window.
      lastDragEndedAtRef.current = Date.now();

      // Column drag — compute new ordered list via splice and call back to
      // the parent. The parent renumbers `order` contiguously and persists.
      // We ALSO apply the new order optimistically in local state so the
      // dropped column lands at its new DOM slot in the same render as the
      // drop, eliminating the brief snap-back jitter that would otherwise
      // happen during the parent mutation's async tick.
      if (active.data.current?.type === 'column') {
        setDraggedColumnId(null);
        if (!over) {
          return;
        }
        const activeId = String(active.id);
        const overId = String(over.id);
        if (activeId === overId) {
          return;
        }
        const currentIds = stageIdsRef.current;
        const fromIndex = currentIds.indexOf(activeId);
        const toIndex = currentIds.indexOf(overId);
        if (fromIndex === -1 || toIndex === -1) {
          return;
        }
        const nextIds = arrayMove(currentIds, fromIndex, toIndex);
        logger.debug('pipeline-dnd: column reorder drop', {
          columnId: activeId,
          fromIndex,
          toIndex,
          nextOrder: nextIds,
        });
        setOptimisticStageOrder(nextIds);
        onReorderStagesRef.current?.(nextIds, () => {
          setOptimisticStageOrder(null);
          logger.warn(
            'pipeline-dnd: column reorder rolled back after API failure'
          );
        });
        return;
      }

      // Read from refs to avoid depending on dragState/activeItem state,
      // which change on every dragOver and would recreate this callback
      const currentActiveItem = activeItemRef.current;
      const currentDragState = dragStateRef.current;

      let targetStage: string | null = null;
      // Which branch resolved the drop target — logged so a wrong-column drop
      // can be traced to its resolution path (direct column hit vs the
      // dragOver-tracked stage vs the hovered card's stage).
      let targetResolution: 'none' | 'column' | 'overStage' | 'card' = 'none';

      if (over) {
        const overId = String(over.id);
        if (stageIdSet.has(overId)) {
          targetStage = overId;
          targetResolution = 'column';
        } else if (currentDragState.overStage) {
          targetStage = currentDragState.overStage;
          targetResolution = 'overStage';
        } else {
          const targetItem = itemByIdMap.get(overId);
          if (targetItem) {
            targetStage = getItemStage(targetItem);
            targetResolution = 'card';
          }
        }
      }

      logger.debug('pipeline-dnd: drag end', {
        activeId: currentActiveItem
          ? String(getItemId(currentActiveItem))
          : null,
        overId: over ? String(over.id) : null,
        targetStage,
        targetResolution,
        originalStage: currentActiveItem
          ? getItemStage(currentActiveItem)
          : null,
      });

      if (currentActiveItem && targetStage) {
        // Expand a collapsed target column now that the drop has committed —
        // the user should see where their card landed. Deliberately on DROP,
        // not on drag-over: expanding mid-drag shifts every column to the
        // right of the expansion against dnd-kit's drag-start rect snapshot,
        // making later drops resolve one column off (see the note in
        // handleDragOver).
        const resolvedTargetStage = targetStage;
        setCollapsedColumns(prev => {
          if (!prev.has(resolvedTargetStage)) {
            return prev;
          }
          const next = new Set(prev);
          next.delete(resolvedTargetStage);
          return next;
        });

        const originalStage = getItemStage(currentActiveItem);
        if (targetStage !== originalStage) {
          const itemId = getItemId(currentActiveItem);

          // The badge explains WHY the card landed where it did, which is true
          // of every cross-stage drop — so it fires unconditionally.
          //
          // It used to be set only when the position was going to change, which
          // made it look like it rendered at random: drop a card near where it
          // already sorts and you got silence, with nothing to distinguish that
          // from a bug.
          triggerResortNotice(itemId, targetStage);

          // The card has already re-sorted in this same commit (itemsByStage
          // sorts every column unconditionally), so the flash marks where it
          // actually is. Nothing is deferred and nothing scrolls.
          setHighlightedItemId(itemId);
        }
      }

      setActiveItem(null);

      try {
        await Promise.race([
          onDragEndHook(targetStage),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Drag-end API timeout')), 30000)
          ),
        ]);
      } catch (error) {
        logger.error(
          'pipeline-dnd: drag-end API call failed',
          error instanceof Error ? error : new Error(String(error)),
          {
            targetStage,
            itemId: currentActiveItem ? getItemId(currentActiveItem) : null,
          }
        );
      }
    },
    [
      stageIdSet,
      itemByIdMap,
      getItemId,
      getItemStage,
      onDragEndHook,
      triggerResortNotice,
    ]
  );

  const handleCancel = useCallback(() => {
    lastDragEndedAtRef.current = Date.now();
    logger.debug('pipeline-dnd: drag cancel', {
      activeItemId:
        activeItemRef.current !== null
          ? String(getItemId(activeItemRef.current))
          : null,
      draggedColumnId: draggedColumnIdRef.current,
    });
    handleDragCancel();
    setActiveItem(null);
    setDraggedColumnId(null);
  }, [handleDragCancel, getItemId]);

  // Stage label lookup — used by announcements
  const stageLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const stage of stages) {
      map.set(stage.id, stage.label);
    }
    return map;
  }, [stages]);

  // Accessibility announcements
  const announcements = useMemo(
    () => buildDragAnnouncements(itemByIdMap, stageLabelMap, getItemStage),
    [itemByIdMap, stageLabelMap, getItemStage]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleCancel}
      autoScroll={isMobile ? false : undefined}
      accessibility={{ announcements }}
    >
      <DndActiveSentinel onActiveNull={handleDndActiveCleared} />
      <div
        ref={scrollContainerRef}
        onClickCapture={suppressPostDragClickCapture}
        className={
          isMobile
            ? `flex gap-4 overflow-x-auto overflow-y-hidden px-4 pt-1 pb-4 flex-1 min-h-0 snap-x ${activeItem || draggedColumnId ? 'snap-none' : 'snap-mandatory'}`
            : 'flex gap-4 overflow-x-auto overflow-y-hidden px-4 pt-1 pb-4 flex-1 min-h-0'
        }
        role='region'
        aria-label='Pipeline board'
      >
        {/* Horizontal sortable context for column reorder. Always mounted —
            individual columns disable their draggable behavior when reorder
            isn't permitted, but they remain droppable so card-into-column
            drops keep working. */}
        <SortableContext
          items={stageIds}
          strategy={horizontalListSortingStrategy}
        >
          {effectiveStages.map((stage, idx) => (
            <PipelineColumn
              key={stage.id}
              stage={stage}
              items={itemsByStage[stage.id] || []}
              getItemId={getItemId}
              renderCard={renderCard}
              onItemClick={onItemClick}
              count={
                stageMetrics?.[stage.id]?.count ??
                (itemsByStage[stage.id] || []).length
              }
              activeItemId={activeItem ? getItemId(activeItem) : null}
              isDragActive={!!activeItem}
              onAddToStage={onAddToStage}
              isCollapsed={collapsedColumns.has(stage.id)}
              onToggleCollapse={toggleColumnCollapse}
              loading={loading}
              isMobile={isMobile}
              onRenameStage={onRenameStage}
              onMoveStage={onMoveStage}
              onInsertStage={
                showAddStageButton ? handleOpenInsertStage : undefined
              }
              onRecolorStage={onRecolorStage}
              onDeleteStage={onDeleteStage}
              canEditStage={canEditStage}
              canDeleteStage={canDeleteStage}
              canReorderColumns={canReorderColumns}
              isFirst={idx === 0}
              isLast={idx === effectiveStages.length - 1}
              isBelowStageMinimum={effectiveStages.length < MIN_PIPELINE_STAGES}
              isUpdating={isUpdating}
              isFiltered={isFiltered}
              footerLines={stageFooterLines[stage.id]}
              resortingLabel={sortLabel}
              resortNoticeItemIds={
                resortNoticeItemIdsByStage[stage.id] ?? EMPTY_ID_SET
              }
              exitingNoticeIds={
                exitingNoticeIdsByStage[stage.id] ?? EMPTY_ID_SET
              }
              highlightedItemId={
                highlightedItemStageId === stage.id ? highlightedItemId : null
              }
              openItemId={openItemStageId === stage.id ? openItemId : null}
            />
          ))}
        </SortableContext>

        {showAddStageButton && (
          <button
            type='button'
            onClick={handleOpenAddStage}
            aria-label='Add stage'
            data-testid='add-stage-button'
            data-component='action-button'
            data-instance='add-stage'
            className='flex flex-shrink-0 items-center justify-center gap-2 rounded-lg border border-dashed border-[var(--color-border-secondary)] bg-transparent px-4 py-3 text-sm font-medium text-[var(--color-text-tertiary)] transition-colors hover:border-[var(--color-border-primary)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-secondary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand h-min self-start cursor-pointer'
          >
            <Plus className='size-4' />
            <span>Add stage</span>
          </button>
        )}
      </div>

      {showAddStageButton && (
        <AddStageModal
          isOpen={isAddStageModalOpen}
          onClose={handleCloseAddStage}
          onSubmit={handleAddStageSubmit}
          showProbability={showProbability}
        />
      )}

      <DragOverlay dropAnimation={null}>
        {activeItem ? (
          <PipelineDragOverlay>
            <div
              className={`${isMobile ? 'w-[85vw]' : 'w-80'} rounded-[5px] border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] dark:bg-bg-tertiary pt-3 pb-2 px-0 shadow-sm`}
            >
              {renderCard(activeItem, true)}
            </div>
          </PipelineDragOverlay>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
